import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Tooltip } from '@radix-ui/themes';

import Button from '../../../../shared/Button';
import useDropdown from '../../../../lib/hooks/useDropdown';
import Text from '../../../../shared/Text';
import { resetScrollPosition } from '../../../../lib/redux/campaignScroll/campaignScrollSlice';

import { ReviewLinkModal } from '../ReviewLinkModal.jsx';
import { Modal } from '../../../boardSetup/components/Modals/Modal.jsx';

export const EditButton = () => {
  const { t } = useTranslation('campaigns');
  return (
    <Button width="108px" mode="contained" variant="primary">
      <PenIcon />
      {t('preview.edit')}
    </Button>
  );
};

export const PreviewButton = ({ url, onClick, isDeleted = false }) => {
  const history = useHistory();
  const { t } = useTranslation('campaigns');
  const {
    dropdownRef,
    buttonRef,
    showDropdown,
    setShowDropdown,
  } = useDropdown();
  const [showModal, setShowModal] = useState(false);

  const handleOnClick = () => {
    history.push(url);
    if (onClick) {
      onClick();
    }
  };

  const renderButton = disabled => (
    <Button
      width="134px"
      mode="outline"
      variant="black"
      ref={buttonRef}
      disabled={disabled}
      onClick={() => !disabled && setShowDropdown(state => !state)}
    >
      <EyeIcon />
      {t('preview.preview')}
      {showDropdown ? <UpArrow /> : <DownArrow />}
    </Button>
  );

  return (
    <>
      {isDeleted ? (
        <Tooltip content={t('edit.previewActions.previewDisabled')}>
          <span>{renderButton(true)}</span>
        </Tooltip>
      ) : (
        renderButton(false)
      )}

      {showDropdown && (
        <PreviewDropdown ref={dropdownRef}>
          <StyledListItem onClick={handleOnClick}>
            <Text variant="subtitle2">
              {t('edit.previewActions.adminPreview')}
            </Text>
          </StyledListItem>
          <StyledListItem onClick={() => setShowModal(true)}>
            <Text variant="subtitle2">
              {t('edit.previewActions.internalReviewLink')}
            </Text>
          </StyledListItem>
        </PreviewDropdown>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <ReviewLinkModal onClose={() => setShowModal(false)} />
      </Modal>
    </>
  );
};

export const AllCampaignsButton = ({ onClick }) => {
  const { t } = useTranslation('campaigns');
  const dispatch = useDispatch();

  const handleClick = () => {
    dispatch(resetScrollPosition());
    if (onClick) {
      onClick();
    }
  };

  return (
    <Button mode="text" variant="black" onClick={handleClick}>
      <LeftArrow />
      {t('welcome.allCampaigns')}
    </Button>
  );
};

const PenIcon = styled.i.attrs({
  className: 'fa-light fa-pen fa-lg',
})`
  margin-right: 6px;
`;

const EyeIcon = styled.i.attrs({
  className: 'fa-light fa-eye fa-lg',
})`
  margin-right: 6px;
`;

const LeftArrow = styled.i.attrs({
  className: 'fa-regular fa-chevron-left',
})`
  margin-right: 6px;
`;

const DownArrow = styled.i.attrs({
  className: 'fa-regular fa-chevron-down fa-lg',
})`
  margin-top: 3px;
  margin-left: 5px;
`;

const UpArrow = styled.i.attrs({
  className: 'fa-regular fa-chevron-up fa-lg',
})`
  margin-top: 3px;
  margin-left: 5px;
`;

const PreviewDropdown = styled.ul`
  margin-top: 50px;
  position: absolute;
  border-radius: 8px;
  background: white;
  width: 175px;
  font-weight: 500;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  z-index: 1;
`;

const StyledListItem = styled.li`
  padding: 13px;
  width: 100%;
  cursor: pointer;
  &:hover {
    background: ${themeGet('colors.primary.10')};
  }
`;
